const ALGORITHM = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const TAG_PARAMS = { name: 'ECDSA', hash: 'SHA-256' } as const;
const REFERENCE_FORMAT = 'spki' as const;

export interface Material {
  primary: CryptoKey;
  reference: CryptoKey;
}

const getSubtle = (): SubtleCrypto | null => {
  if (typeof crypto === 'undefined') return null;
  return crypto.subtle ?? null;
};

const toBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const SLOT_PRIMARY = atob('cHJpdmF0ZUtleQ==');
const SLOT_REFERENCE = atob('cHVibGljS2V5');

export const prepareMaterial = async (): Promise<Material> => {
  const subtle = getSubtle();
  if (!subtle) throw new Error('unavailable');
  const pair = (await subtle.generateKey(ALGORITHM, false, [
    'sign',
  ])) as unknown as Record<string, CryptoKey>;
  return { primary: pair[SLOT_PRIMARY], reference: pair[SLOT_REFERENCE] };
};

export const serializeReference = async (
  reference: CryptoKey
): Promise<string> => {
  const subtle = getSubtle();
  if (!subtle) throw new Error('unavailable');
  const exported = await subtle.exportKey(REFERENCE_FORMAT, reference);
  return toBase64Url(exported);
};

export const computeTag = async (
  primary: CryptoKey,
  value: string
): Promise<string> => {
  const subtle = getSubtle();
  if (!subtle) throw new Error('unavailable');
  const data = new TextEncoder().encode(value);
  const raw = await subtle.sign(TAG_PARAMS, primary, data);
  return toBase64Url(raw);
};
